import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** One thing a report can be built from: the journal, a Launchpad run or a backtest. */
export type ReportSourceOption = {
  value: string;
  label: string;
  /** Group heading: "Your broker journal", a strategy's name, or "Not saved as a strategy". */
  group: string;
  /** Second line, e.g. "Launchpad · paper · 12 trades". */
  detail?: string;
  /** The account its trades are on (read-only in the wizard), or null for paper / backtests. */
  accountLabel?: string | null;
  /** Newest first within "Not saved as a strategy"; only the first few show until you search. */
  recentRank?: number;
  /** Shown but not selectable (e.g. no closed trades yet). */
  disabled?: boolean;
};

const UNSAVED_GROUP = "Not saved as a strategy";
const JOURNAL_GROUP = "Your broker journal";
const UNSAVED_SHOWN = 10;

/**
 * The report's "Trades from": a searchable list grouped by strategy, so a long history of
 * backtests and runs stays findable. Unsaved backtests show the most recent few until you search.
 */
export function ReportSourcePicker({
  value,
  options,
  onChange,
}: {
  value: string;
  options: ReportSourceOption[];
  onChange: (value: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value) ?? options[0];

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (o: ReportSourceOption) =>
      !q || `${o.label} ${o.detail ?? ""} ${o.group}`.toLowerCase().includes(q);
    const by = new Map<string, ReportSourceOption[]>();
    let hiddenUnsaved = 0;
    for (const o of options) {
      if (!match(o)) continue;
      if (!q && o.group === UNSAVED_GROUP && (o.recentRank ?? 0) >= UNSAVED_SHOWN) {
        hiddenUnsaved += 1;
        continue;
      }
      by.set(o.group, [...(by.get(o.group) ?? []), o]);
    }
    // The journal first, saved strategies next, unsaved runs and backtests last.
    const rank = (g: string) => (g === JOURNAL_GROUP ? 0 : g === UNSAVED_GROUP ? 2 : 1);
    const list = Array.from(by.entries()).sort((a, b) => rank(a[0]) - rank(b[0]));
    return { list, hiddenUnsaved };
  }, [options, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="!flex h-10 w-full items-center justify-between gap-2 !rounded-md !border !border-solid !border-input bg-background !px-3 text-left text-sm"
          data-report-source-picker=""
        >
          <span className="min-w-0 truncate">{selected?.label ?? "Your broker journal"}</span>
          <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(420px,calc(100vw-2rem))] p-0">
        <div className="flex items-center gap-2 border-b border-solid border-border px-3 py-2">
          <Search className="size-4 text-muted-foreground" aria-hidden />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search strategies, runs and backtests"
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-[360px] overflow-y-auto py-1" role="listbox">
          {groups.list.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">Nothing matches “{query}”.</p>
          ) : (
            groups.list.map(([group, items]) => (
              <div key={group} className="py-1">
                <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {group}
                </p>
                {items.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    disabled={o.disabled}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={cn(
                      "!flex w-full items-start gap-2 !px-3 !py-1.5 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
                      o.value === value && "bg-muted/60"
                    )}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{o.label}</span>
                      {o.detail ? <span className="truncate text-xs text-muted-foreground">{o.detail}</span> : null}
                    </span>
                    {o.value === value ? <Check className="mt-0.5 size-4 shrink-0" aria-hidden /> : null}
                  </button>
                ))}
              </div>
            ))
          )}
          {groups.hiddenUnsaved > 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {groups.hiddenUnsaved} older unsaved backtest{groups.hiddenUnsaved === 1 ? "" : "s"} — search to find them.
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
